import React, { useState } from "react"
import { Button, Table, Tag, Flex, Card } from "antd"
import { DeleteOutlined, CheckOutlined } from "@ant-design/icons"
import { ConfirmModal } from "ui/ConfirmModal"
import { useTranslation } from "react-i18next"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import NoAssignedExperience from "ui/Icon/DSIcon/NoAssignedExperience"
import styles from "./index.module.css"
import PropTypes from "prop-types"
import { colors } from "themes/variables"
import { Text } from "ui/Typography"

const AssignedExperiences = ({ experiences = [], onRemove, onAssign }) => {
  const { t } = useTranslation()
  const [experienceToRemove, setExperienceToRemove] = useState(null)
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false)
  const [experienceToRemoveName, setExperienceToRemoveName] = useState("")

  const handleAssign = () => {
    if (onAssign) {
      onAssign()
    }
  }

  /**
   * Show confirmation modal before removing an experience
   */
  const handleRemoveClick = (experienceIdToRemove) => {
    // Prevent removal if this is the only experience
    if (experiences.length === 1) {
      return
    }

    // Find the experience name for the confirmation message
    const experienceToDelete = experiences.find((exp) => exp.id === experienceIdToRemove)
    const experienceName = experienceToDelete?.title || experienceToDelete?.name || "this experience"

    // Store the ID and name, then show confirmation modal
    setExperienceToRemove(experienceIdToRemove)
    setExperienceToRemoveName(experienceName)
    setShowRemoveConfirmModal(true)
  }

  /**
   * Actually remove the experience after confirmation
   */
  const handleConfirmRemove = () => {
    if (experienceToRemove && onRemove) {
      onRemove(experienceToRemove)
    }
    setShowRemoveConfirmModal(false)
    setExperienceToRemove(null)
    setExperienceToRemoveName("")
  }

  /**
   * Handle cancellation of experience removal
   */
  const handleCancelRemove = () => {
    setShowRemoveConfirmModal(false)
    setExperienceToRemove(null)
    setExperienceToRemoveName("")
  }

  // Table columns for assigned experiences
  const experienceColumns = [
    {
      title: t("equipment.details.columns.experience_name", { defaultValue: "Experience Name" }),
      dataIndex: "title",
      key: "title",
      render: (_, record) => record.title || record.name
    },
    {
      title: t("equipment.details.columns.guests", { defaultValue: "Guests" }),
      dataIndex: "max_guests",
      key: "max_guests",
      render: (_, record) => record.max_guests || "-"
    },
    {
      title: t("equipment.details.columns.price", { defaultValue: "Price" }),
      key: "price",
      render: (_, record) => {
        const minPrice = Number.parseFloat(record.min_price || 0)
        const maxPrice = Number.parseFloat(record.max_price || 0)
        const currency = record.currency || "$"

        if (minPrice === maxPrice) {
          return `${currency}${minPrice.toFixed(2)}`
        }
        return `${currency}${minPrice.toFixed(2)} - ${currency}${maxPrice.toFixed(2)}`
      }
    },
    {
      title: t("equipment.details.columns.status", { defaultValue: "Status" }),
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const isPublished = status?.toLowerCase() === "published"
        const displayStatus = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : "Draft"
        return (
          <Tag color={isPublished ? "green" : "default"} icon={isPublished ? <CheckOutlined /> : null} className={styles.statusTag}>
            {displayStatus}
          </Tag>
        )
      }
    },
    {
      title: "",
      key: "action",
      align: "right",
      width: 50,
      render: (_, record) => {
        const isOnlyExperience = experiences.length === 1
        return (
          <Button
            type="text"
            icon={<DeleteOutlined />}
            className={styles.deleteButton}
            onClick={() => handleRemoveClick(record.id)}
            disabled={isOnlyExperience}
            title={
              isOnlyExperience
                ? t("equipment.details.cannot_remove_last_experience", { defaultValue: "Cannot remove the last experience" })
                : ""
            }
          />
        )
      }
    }
  ]

  return (
    <Flex vertical gap={16} className={styles.container}>
      {/* Header */}
      <Flex align="center" justify="space-between" className={styles.header}>
        <h2 className={styles.title}>
          {t("equipment.details.assigned_experiences", { defaultValue: "Assigned Experiences" })}{" "}
          <span className={styles.count}>({experiences.length})</span>
        </h2>
        <Button type="link" onClick={handleAssign} className={styles.assignButton}>
          {t("equipment.details.assign", { defaultValue: "Assign" })}
        </Button>
      </Flex>

      {/* Table with Empty State */}
      <WithEmptyStateWrapper
        name="assigned experience"
        hasItems={experiences.length > 0}
          fallback={
          <Card className="py-5xl">
            <Flex vertical justify="center" align="center" gap={12}>
              <NoAssignedExperience />
              <Text color={colors.textPrimary} weight="semibold">
                {t("crewMember.experiences.emptyState.title")}
              </Text>
              <Text color={colors.textSecondaryDark}>{t("crewMember.experiences.emptyState.subtitle")}</Text>
            </Flex>
          </Card>
        }
      >
        <div className={styles.scrollContainer}>
          <Table dataSource={experiences} columns={experienceColumns} pagination={false} rowKey="id" className={styles.experiencesTable} />
        </div>
      </WithEmptyStateWrapper>

      {/* Remove Experience Confirmation Modal */}
      <ConfirmModal
        open={showRemoveConfirmModal}
        title={t("equipment.remove_experience", { defaultValue: "Remove Experience" })}
        info={t("equipment.confirm_remove_experience_text", {
          defaultValue: `Are you sure you want to remove <strong>${experienceToRemoveName}</strong> from this crew member?`,
          experienceName: experienceToRemoveName
        })}
        onCancel={handleCancelRemove}
        onDelete={handleConfirmRemove}
        deleteLabel={t("common.remove", { defaultValue: "Remove" })}
        cancelLabel={t("common.cancel", { defaultValue: "Cancel" })}
        hideCancelButton={false}
      />
    </Flex>
  )
}

AssignedExperiences.propTypes = {
  experiences: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      title: PropTypes.string,
      name: PropTypes.string,
      max_guests: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      min_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      max_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      currency: PropTypes.string,
      status: PropTypes.string
    })
  ),
  onRemove: PropTypes.func,
  onAssign: PropTypes.func
}

export default AssignedExperiences
